import { UseFormReturn } from 'react-hook-form';

import { DisputeStatus } from '../../../../backend/src/types/types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Textarea } from '@/new/components/shadcn/textarea';
import { handleDownloadCaseIssuesSheet } from '@/new/pages/client-materials/helpers';
import { DetailsForm } from '@/new/pages/matters/new/schema';

interface DetailsInputProps {
  form: UseFormReturn<DetailsForm>;
  isGeneratingDetails?: boolean;
  disabled?: boolean;
}

export function DetailsInput({ form, isGeneratingDetails, disabled }: DetailsInputProps) {
  const { fetchConfigGET } = useGetFetchConfig();

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="issues"
        render={({ field }) => (
          <FormItem>
            <FormMessage />

            <FormLabel required>Matter Issues</FormLabel>
            <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">
              Issues provide context that Wexler uses to help determine the relevance and significance of facts. Well
              formulated issues improve Wexler's understanding of your matter. For more information and guidance on
              writing issues,{' '}
              <button
                onClick={() => handleDownloadCaseIssuesSheet(fetchConfigGET)}
                className="text-blue-500 duration-150 hover:text-blue-600"
              >
                download the crib sheet
              </button>
              . Limited to 2000 characters.
            </p>

            <FormControl>
              <div className="relative">
                {isGeneratingDetails && (
                  <>
                    <Skeleton className="absolute top-3 left-3 z-10 h-4 w-3/5 bg-gray-100" />
                    <Skeleton className="absolute top-8 left-3 z-10 h-4 w-4/5 bg-gray-100" />
                  </>
                )}
                <Textarea
                  {...field}
                  data-testid="matter-creator-description-field"
                  placeholder={
                    isGeneratingDetails
                      ? ''
                      : 'Breach of Contract: Jane Doe failed to deliver goods by the agreed date of the 5th of September'
                  }
                  className={`${isGeneratingDetails ? 'text-transparent' : ''} min-h-48`}
                  disabled={disabled}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <hr className="my-10" />

      <FormField
        control={form.control}
        name="disputeType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Matter Type <span className="font-normal text-gray-400">Optional</span>
            </FormLabel>
            <FormControl>
              <div className="relative">
                {isGeneratingDetails && <Skeleton className="absolute inset-3 z-10 h-4 w-3/5 bg-gray-100" />}
                <Input
                  {...field}
                  data-testid="matter-creator-type-field"
                  placeholder={isGeneratingDetails ? '' : 'Arbitration, Employment Dispute, Commercial Litigation'}
                  className={isGeneratingDetails ? 'text-transparent' : ''}
                  disabled={disabled}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="userDefinedEntities"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Entities <span className="font-normal text-gray-400">Optional</span>
              <p className="mt-2 text-xs whitespace-normal text-gray-500">
                Entities might be key persons or organisations.
              </p>
            </FormLabel>
            <FormControl>
              <div className="relative">
                {isGeneratingDetails && <Skeleton className="absolute inset-3 z-10 h-4 w-2/5 bg-gray-100" />}
                <Input
                  {...field}
                  data-testid="matter-creator-entities-field"
                  placeholder={isGeneratingDetails ? '' : 'John Doe, Acme Corporation'}
                  className={isGeneratingDetails ? 'text-transparent' : ''}
                  disabled={disabled}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="additionalContext"
        render={({ field }) => (
          <FormItem>
            <FormMessage />

            <FormLabel>
              Additional Context <span className="font-normal text-gray-400">Optional</span>
              <p className="mt-2 text-xs whitespace-normal text-gray-500">
                Any additional information surrounding this matter that may be useful in determining the relevance of
                facts.
              </p>
            </FormLabel>
            <FormControl>
              <Textarea
                {...field}
                data-testid="matter-creator-context-field"
                placeholder=""
                className="min-h-24"
                rows={3}
                disabled={disabled}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="timePeriod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Time Period <span className="font-normal text-gray-400">Optional</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                data-testid="matter-creator-time-period-field"
                placeholder='e.g. "1960 to 1990" or "Early 2000s"...'
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="disputeStatus"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Dispute Status <span className="font-normal text-gray-400">Optional</span>
            </FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
              <FormControl>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select a dispute status..." />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.values(DisputeStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
